import { BodyRegular, Headline5, WrappingText } from 'components/Elements';
import { MainSectionWithGaps } from 'components/sections/MainSection';
import useGetRequest from 'hooks/useGetRequest';
import React, { ReactNode } from 'react';
import strings from '../../../strings/strings.json';
import Notification, { NotificationType } from '../../../components/notifiers/Notification';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../../theme';
import Table, { TableRow } from 'components/table/Table';
import { EmptyTableContent } from 'components/table/EmptyTableContent';
import Button from '../../../components/buttons/Button';
import BinIcon from 'assets/icons/controls/BinIcon';
import Alert from '../../../components/alert/Alert';
import useDeleteRequest from '../../../hooks/useDeleteRequest';
import { logError } from '../../../lib/debug-helpers';
import { IconSize } from 'assets/icons/icon-sizes';
import ScheduleIcon from 'assets/icons/pathway/ScheduleIcon';
import { RoleType, UserModel } from 'models/User';
import { AuthContext } from 'contextProviders/AuthContext';
import Header from 'components/sections/Header';
import NavBackButton from 'components/buttons/NavBackButton';
import { OADashboardContext } from 'contextProviders/OADashboardContext';

export function UserDetailsPage(): JSX.Element {
    const navigate = useNavigate();
    const params = useParams();
    const { id } = params;
    const { userData } = React.useContext(AuthContext);
    const { refetchUsers } = React.useContext(OADashboardContext);

    const [removeUserWarningVisible, setRemoveUserWarningVisible] = React.useState<boolean>(false);
    const [removeUserError, setRemoveUserError] = React.useState<boolean>(false);

    const { data: user, errors } = useGetRequest<UserModel>(`organisations/users/${id}`);
    const { deleteData: removeUserData, deleteInProgress: removingUser } = useDeleteRequest(
        `organisations/users/${id}`,
    );

    if (!user) return <></>;
    if (errors && errors.length > 0) {
        return (
            <UserDetailsBase>
                <Notification
                    notificationType={NotificationType.DANGER}
                    title={strings.OAUserDetailsPage.getTrainerError}
                />
            </UserDetailsBase>
        );
    }

    async function removeUser() {
        const response = await removeUserData();
        if (response.errors) {
            logError(response.errors);
            setRemoveUserError(true);
            return;
        }
        await refetchUsers();
        navigate('/organisation/dashboard', { state: { activeTabId: 'users' } });
        return;
    }

    const userMayBeRunningLiveCourses = user.roles.some(
        (role) => role.name === RoleType.ORGANISATION_ADMIN || role.name === RoleType.TRAINER,
    );

    let courseScheduleRows: TableRow[] = [];

    if (userMayBeRunningLiveCourses) {
        courseScheduleRows = (user.trainerCourseSchedules ?? []).map((scheduledCourse) => {
            return {
                id: scheduledCourse.id,
                cells: [
                    {
                        name: strings.OAUserDetailsPage.scheduledCourses.course.title,
                        value: scheduledCourse.course.title,
                        isBold: true,
                    },
                    {
                        name: strings.OAUserDetailsPage.scheduledCourses.course.startDate,
                        value: new Date(scheduledCourse.startDate).toLocaleDateString(),
                    },
                    {
                        name: strings.OAUserDetailsPage.scheduledCourses.course.startDate,
                        value: new Date(scheduledCourse.endDate).toLocaleDateString(),
                    },
                ],
            };
        });
    }

    const userName = `${user.firstName} ${user.lastName}`;

    return (
        <UserDetailsBase>
            {removeUserWarningVisible && (
                <Alert
                    buttonText={strings.OAUserDetailsPage.removeAlert.buttonText}
                    alertText={strings.OAUserDetailsPage.removeAlert.alertText}
                    closeAlert={() => setRemoveUserWarningVisible(false)}
                    ariaButtonText={strings.OAUserDetailsPage.removeAlert.buttonAria}
                    onSubmit={removeUser}
                    error={
                        removeUserError
                            ? {
                                  title: strings.OAUserDetailsPage.removeError.title,
                                  description: strings.OAUserDetailsPage.removeError.description,
                              }
                            : undefined
                    }
                />
            )}
            <UserDetailsCard>
                <Row>
                    <RowTitle>
                        <BodyRegular $bold>{strings.OAUserDetailsPage.informationCard.name}</BodyRegular>
                    </RowTitle>
                    <WrappingText>{userName}</WrappingText>
                </Row>
                <Row>
                    <RowTitle>
                        <BodyRegular $bold>{strings.OAUserDetailsPage.informationCard.emailAddress}</BodyRegular>
                    </RowTitle>
                    <WrappingText>{user.email}</WrappingText>
                </Row>
            </UserDetailsCard>
            {userMayBeRunningLiveCourses && (
                <div>
                    <HeadingRow>
                        <RowTitle>
                            <Headline5>{strings.OAUserDetailsPage.scheduledCourses.heading}</Headline5>
                        </RowTitle>
                    </HeadingRow>
                    <Table
                        headers={[
                            {
                                name: strings.OAUserDetailsPage.scheduledCourses.course.title,
                                rowPercentage: 50,
                            },
                            {
                                name: strings.OAUserDetailsPage.scheduledCourses.course.startDate,
                                rowPercentage: 25,
                            },
                            {
                                name: strings.OAUserDetailsPage.scheduledCourses.course.endDate,
                                rowPercentage: 25,
                            },
                        ]}
                        rows={courseScheduleRows}
                        emptyTableContent={
                            <EmptyTableContent
                                icon={<ScheduleIcon size={IconSize.XLARGE} colour={theme.textColour} />}
                                title={strings.OAUserDetailsPage.emptyTable.title}
                                description={strings.OAUserDetailsPage.emptyTable.description}
                            />
                        }
                    />
                </div>
            )}
            {courseScheduleRows.length > 0 &&
                !user.roles.some((role) => role.name === RoleType.ORGANISATION_ADMIN) && (
                    <Notification
                        notificationType={NotificationType.WARNING}
                        title={strings.OAUserDetailsPage.removeUserWarningTitle}
                        description={strings.OAUserDetailsPage.removeUserWarningDetail}
                        maxWidth="100%"
                    />
                )}
            {userData.activeRole === RoleType.ORGANISATION_ADMIN &&
                !user.roles.some((role) => role.name === RoleType.ORGANISATION_ADMIN) && (
                    <Button
                        title={strings.OAUserDetailsPage.removeButton.title}
                        aria={strings.OAUserDetailsPage.removeButton.aria}
                        icon={<BinIcon />}
                        onClick={() => setRemoveUserWarningVisible(true)}
                        submitting={removingUser}
                    />
                )}
        </UserDetailsBase>
    );
}

type UserDetailsSectionProps = {
    children: ReactNode;
};

const UserDetailsBase = ({ children }: UserDetailsSectionProps): JSX.Element => {
    return (
        <>
            <Header
                title={strings.OAUserDetailsPage.heading}
                aboveTitleElement={
                    <NavBackButton
                        backButtonTitle={strings.OAUserDetailsPage.backButton.title}
                        backButtonAria={strings.OAUserDetailsPage.backButton.aria}
                        backButtonHref={'/organisation/dashboard'}
                        backButtonNavigateState={{ state: { activeTabId: 'users' } }}
                    />
                }
            />
            <MainSectionWithGaps>{children}</MainSectionWithGaps>
        </>
    );
};

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 80px;
    min-height: 65px;
    border-bottom: 1px solid ${theme.cardBorder};
    padding: 0 ${sizes.spacingMd};

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        justify-content: center;
        min-height: 80px;
        align-items: unset;
        gap: ${sizes.spacingSm};
    }
`;

const HeadingRow = styled(Row)`
    padding-bottom: ${sizes.spacingSm};
    @media (max-width: ${breakpoints.sm}) {
        min-height: 40px;
    }
`;

const RowTitle = styled.div`
    min-width: 125px;

    @media (max-width: ${breakpoints.sm}) {
        min-width: unset;
    }
`;

const UserDetailsCard = styled.div`
    display: flex;
    flex-direction: column;
    width: fit-content;
    background: ${theme.cardBackground};

    @media (max-width: ${breakpoints.sm}) {
        width: unset;
    }
`;
